import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { useShiftPlanFiltersStore } from "@/modules/shift-plan-overview/stores/useShiftPlanFiltersStore"
import { type Country, CountryValues } from "@/types/__generated__/union-types"

export const LicenseFilter = () => {
  const { t } = useTranslation()

  const licenses = useShiftPlanFiltersStore((state) => state.filters.licenses)
  const setLicenses = useShiftPlanFiltersStore((state) => state.actions.setLicenses)
  const isLoading = useShiftPlanFiltersStore((state) => state.isLoading)
  const count = licenses.length

  const handleCheckChange = (item: Country) => {
    const index = licenses.indexOf(item)
    const newLicenses = [...licenses]

    if (index < 0) newLicenses.push(item)
    else newLicenses.splice(index, 1)

    setLicenses(newLicenses)
  }

  const handleReset = () => {
    setLicenses([])
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={isLoading}
        className={cn(
          "aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer",
          isLoading && "cursor-not-allowed!",
        )}>
        <Chip data-testid="shift-plan-license-filter-trigger" className="h-8 gap-0.5" checked={count > 0} type="button">
          <div className={`overflow-hidden transition-all duration-200 ${count > 0 ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">{t("shiftPlanOverview.filters.license") + (count > 0 ? ` (${count})` : ``)}</span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="shift-plan-license-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <div className="grid gap-0.5">
          {CountryValues.map((item) => (
            <DropdownMenuCheckboxItem
              onSelect={(e) => e.preventDefault()}
              className="h-9"
              key={item}
              checked={licenses.includes(item)}
              data-testid={`shift-plan-menu-license-${item.toLowerCase()}`}
              onCheckedChange={() => handleCheckChange(item)}>
              {item}
            </DropdownMenuCheckboxItem>
          ))}
        </div>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={handleReset}
            data-testid="shift-plan-reset-license-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
